/**
 * Manuscript submissions, stored in the shared aimhomoeopathy Firebase project (AJHM area only):
 * - Storage: AJHM/submissions/<submission id>_<file name>
 * - Firestore: ajhm_submissions/<submission id> (author details, file path, status)
 * Rules (firestore.rules / storage.rules) let anyone create a submission and fetch one by its
 * unguessable id, but nobody can list them or change them from the website.
 * Afterwards netlify/functions/submission-email.js emails the author and the editorial office.
 */
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from './firebase';

export const MAX_MANUSCRIPT_BYTES = 100 * 1024 * 1024;

const WORD_TYPES = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const safeFileName = (name) => {
  const lower = String(name || 'manuscript').toLowerCase();
  const ext = (lower.match(/\.([a-z0-9]+)$/) || [])[1] || 'docx';
  const base = lower.replace(/\.[a-z0-9]+$/, '').replace(/[^a-z0-9_-]+/g, '_').replace(/_+/g, '_').slice(0, 80) || 'manuscript';
  return { name: `${base}.${ext}`, ext };
};

const makeTrackingId = (id) => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `AJHM-${ymd}-${id.slice(0, 6).toUpperCase()}`;
};

const clean = (value, max = 500) => String(value || '').trim().slice(0, max);

/**
 * Uploads the manuscript and saves the submission. onProgress(0-100) reports the upload.
 * Returns { id, trackingId, fileName, fileSize, emailSent }.
 */
export async function submitManuscript(file, details, onProgress) {
  if (!file) throw new Error('Please select a manuscript file.');
  if (file.size > MAX_MANUSCRIPT_BYTES) throw new Error('File size too large. Maximum size is 100MB.');
  const { name, ext } = safeFileName(file.name);
  if (!WORD_TYPES[ext]) throw new Error('Please upload a Word document (.doc or .docx).');

  const submissionRef = doc(collection(db, 'ajhm_submissions'));
  const trackingId = makeTrackingId(submissionRef.id);
  const filePath = `AJHM/submissions/${submissionRef.id}_${name}`;

  // 1. File
  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, filePath), file, {
      contentType: WORD_TYPES[ext],
      customMetadata: { trackingId, authorEmail: clean(details.email, 200) },
    });
    task.on(
      'state_changed',
      (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => reject(new Error(
        err?.code === 'storage/unauthorized'
          ? 'The upload was refused. Please check the file is a Word document under 100MB, or contact editor@aimjhm.com or domus.publications@gmail.com.'
          : 'The upload failed. Please check your connection and try again.'
      )),
      resolve
    );
  });

  // 2. Details
  await setDoc(submissionRef, {
    trackingId,
    firstName: clean(details.firstName, 100),
    middleName: clean(details.middleName, 100),
    lastName: clean(details.lastName, 100),
    countryCode: clean(details.countryCode, 6),
    mobile: clean(details.mobile, 20),
    email: clean(details.email, 200),
    address: clean(details.address, 1000),
    fileName: clean(file.name, 200),
    filePath,
    fileSize: file.size,
    status: 'Submitted',
    submittedAt: serverTimestamp(),
  });

  // 3. Confirmation emails (the submission is saved even if this fails)
  let emailSent = false;
  try {
    const res = await fetch('/.netlify/functions/submission-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: submissionRef.id }),
    });
    emailSent = res.ok && (await res.json()).emailSent === true;
  } catch (e) {
    // Email is optional
  }

  return { id: submissionRef.id, trackingId, fileName: file.name, fileSize: file.size, emailSent };
}
